import React from 'react';
import { Box, Paper, Typography, Chip, Stack, Grid, LinearProgress, Link } from '@mui/material';
import { CheckCircle as CheckIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { TrainingInsights, scoreColor, formatScore, barColor } from '../types';

interface Props {
  data: TrainingInsights['competitorHandling'];
  onNavigateToTimestamp?: (timestampIso: string) => void;
}

const CompetitorHandlingSection: React.FC<Props> = ({ data, onNavigateToTimestamp }) => {
  if (!data) return null;

  return (
    <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Competitor Handling</Typography>
        <Chip
          label={formatScore(data.overallScore)}
          color={scoreColor(data.overallScore)}
          size="medium"
        />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {data.overallSummary}
      </Typography>
      {data.overallScore != null && (
        <LinearProgress
          variant="determinate"
          value={data.overallScore}
          color={barColor(data.overallScore)}
          sx={{ mb: 3, height: 8, borderRadius: 1 }}
        />
      )}

      <Stack spacing={2}>
        {data.items?.map((item, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="subtitle2">{item.competitorName}</Typography>
              <Chip
                label={`${item.handlingScore}%`}
                size="small"
                color={scoreColor(item.handlingScore)}
              />
              {item.timestampIso && onNavigateToTimestamp && (
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => onNavigateToTimestamp(item.timestampIso!)}
                  sx={{ ml: 'auto' }}
                >
                  View in timeline &rarr;
                </Link>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {item.handlingSummary}
            </Typography>
            <Grid container spacing={2}>
              {item.advantagesUsed?.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="success.main" fontWeight={600} gutterBottom>
                    Advantages used
                  </Typography>
                  {item.advantagesUsed.map((a, i) => (
                    <Box key={i} display="flex" gap={0.5} alignItems="flex-start">
                      <CheckIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />
                      <Typography variant="body2">{a}</Typography>
                    </Box>
                  ))}
                </Grid>
              )}
              {item.advantagesMissed?.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="error.main" fontWeight={600} gutterBottom>
                    Advantages missed
                  </Typography>
                  {item.advantagesMissed.map((a, i) => (
                    <Box key={i} display="flex" gap={0.5} alignItems="flex-start">
                      <CancelIcon color="error" fontSize="small" sx={{ mt: 0.25 }} />
                      <Typography variant="body2">{a}</Typography>
                    </Box>
                  ))}
                </Grid>
              )}
            </Grid>
            {item.missedOpportunity && (
              <Box mt={1.5} p={1.5} bgcolor="warning.50" borderRadius={1}>
                <Typography variant="caption" color="warning.main" fontWeight={600}>
                  Missed opportunity
                </Typography>
                <Typography variant="body2">{item.missedOpportunity}</Typography>
              </Box>
            )}
            {item.recommendedApproach && (
              <Box mt={1.5} p={1.5} bgcolor="info.50" borderRadius={1}>
                <Typography variant="caption" color="info.main" fontWeight={600}>
                  Recommended approach
                </Typography>
                <Typography variant="body2">{item.recommendedApproach}</Typography>
              </Box>
            )}
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
};

export default CompetitorHandlingSection;
